import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import Drawer, { Field } from "@/components/Drawer";
import { dealStageMap } from "@/components/StatusBadge";
import { Plus, Zap, IndianRupee, Trash2, Pencil } from "lucide-react";
import { fmtMoney, fmtDate, fmtKW } from "@/lib/format";
import { toast } from "sonner";

const STAGES = ["new", "qualified", "site_survey", "proposal", "negotiation", "won", "lost"];
const empty = {
  title: "", customer_id: "", lead_id: "", stage: "new",
  value: 0, capacity_kw: "", probability: 30, expected_close_date: "",
  owner_id: "", notes: "",
};

export default function Deals() {
  const [rows, setRows] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [drawer, setDrawer] = useState(null);
  const [dragging, setDragging] = useState(null);

  const load = async () => {
    const [d, u, c, l] = await Promise.all([
      api.get("/deals"), api.get("/users"), api.get("/customers"), api.get("/leads"),
    ]);
    setRows(d.data); setUsers(u.data); setCustomers(c.data); setLeads(l.data);
  };
  useEffect(() => { load(); }, []);

  const stageDeals = (s) => rows.filter((d) => d.stage === s);
  const stageTotal = (s) => stageDeals(s).reduce((a, d) => a + (Number(d.value) || 0), 0);

  const onDrop = async (stage) => {
    if (!dragging) return;
    if (dragging.stage === stage) { setDragging(null); return; }
    try {
      await api.patch(`/deals/${dragging.id}/stage`, { stage });
      toast.success(`Moved to ${dealStageMap[stage].label}`);
      setDragging(null);
      load();
    } catch (e) {
      toast.error("Failed to update stage");
    }
  };

  const save = async (data) => {
    const payload = {
      ...data,
      value: Number(data.value) || 0,
      capacity_kw: data.capacity_kw === "" ? null : Number(data.capacity_kw),
      probability: Number(data.probability) || 0,
      customer_id: data.customer_id || null,
      lead_id: data.lead_id || null,
      owner_id: data.owner_id || null,
      expected_close_date: data.expected_close_date || null,
    };
    try {
      if (drawer.mode === "edit") await api.patch(`/deals/${drawer.data.id}`, payload);
      else await api.post("/deals", payload);
      toast.success("Saved");
      setDrawer(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete deal?")) return;
    await api.delete(`/deals/${id}`);
    load();
  };

  return (
    <div data-testid="deals-page">
      <PageHeader
        title="Deals Pipeline"
        subtitle="Drag deals across stages. Track value, probability and MW under negotiation."
        actions={
          <button className="btn-primary flex items-center gap-2" data-testid="add-deal-btn"
            onClick={() => setDrawer({ mode: "create", data: empty })}>
            <Plus className="w-4 h-4" /> New Deal
          </button>
        }
      />

      <div className="flex gap-4 overflow-x-auto pb-4 thin-scroll" data-testid="deals-kanban">
        {STAGES.map((s) => {
          const cfg = dealStageMap[s];
          const list = stageDeals(s);
          return (
            <div
              key={s}
              className="kanban-col"
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => onDrop(s)}
              data-testid={`kanban-col-${s}`}
            >
              <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: cfg.text }} />
                  <span className="font-medium text-slate-800 text-sm">{cfg.label}</span>
                  <span className="text-xs text-slate-500">({list.length})</span>
                </div>
                <span className="text-xs text-slate-500 font-mono">{fmtMoney(stageTotal(s))}</span>
              </div>
              <div className="flex flex-col gap-2 mt-2">
                {list.map((d) => {
                  const customer = customers.find((c) => c.id === d.customer_id);
                  return (
                    <div
                      key={d.id}
                      draggable
                      onDragStart={() => setDragging(d)}
                      onDragEnd={() => setDragging(null)}
                      className={`kanban-card ${dragging?.id === d.id ? "dragging" : ""}`}
                      data-testid={`deal-card-${d.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-medium text-slate-900 text-sm">{d.title}</div>
                        <span className="text-xs font-mono text-slate-500">{d.probability}%</span>
                      </div>
                      {customer && <div className="text-xs text-slate-500 mt-0.5">{customer.name}</div>}
                      <div className="flex items-center gap-3 mt-2 text-xs">
                        <span className="inline-flex items-center gap-1 text-slate-700 font-mono">
                          <IndianRupee className="w-3 h-3" />
                          {(Number(d.value) || 0).toLocaleString("en-IN")}
                        </span>
                        {d.capacity_kw ? (
                          <span className="inline-flex items-center gap-1 text-[#059669]">
                            <Zap className="w-3 h-3" /> {fmtKW(d.capacity_kw)}
                          </span>
                        ) : null}
                      </div>
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                        <span className="text-[11px] text-slate-400">
                          {d.expected_close_date ? `Close ${fmtDate(d.expected_close_date)}` : "—"}
                        </span>
                        <div className="flex gap-1">
                          <button className="btn-ghost !p-1" data-testid={`edit-deal-${d.id}`}
                            onClick={() => setDrawer({ mode: "edit", data: { ...empty, ...d } })}>
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button className="btn-ghost !p-1 text-red-600" data-testid={`delete-deal-${d.id}`}
                            onClick={() => remove(d.id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {drawer && (
        <DealDrawer drawer={drawer} onClose={() => setDrawer(null)} onSave={save}
          users={users} customers={customers} leads={leads} />
      )}
    </div>
  );
}

function DealDrawer({ drawer, onClose, onSave, users, customers, leads }) {
  const [data, setData] = useState(drawer.data);
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));
  return (
    <Drawer open onClose={onClose} title={drawer.mode === "edit" ? "Edit Deal" : "New Deal"}
      testid="deal-drawer"
      footer={
        <>
          <button className="btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn-primary" data-testid="deal-save-btn" onClick={() => onSave(data)}>Save</button>
        </>
      }
    >
      <Field label="Title *"><input className="input-base" value={data.title} data-testid="deal-title" onChange={(e) => set("title", e.target.value)} /></Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
        <Field label="Customer">
          <select className="input-base" value={data.customer_id || ""} onChange={(e) => set("customer_id", e.target.value)}>
            <option value="">—</option>
            {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </Field>
        <Field label="From Lead">
          <select className="input-base" value={data.lead_id || ""} onChange={(e) => set("lead_id", e.target.value)}>
            <option value="">—</option>
            {leads.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </Field>
        <Field label="Stage">
          <select className="input-base" value={data.stage} data-testid="deal-stage" onChange={(e) => set("stage", e.target.value)}>
            {STAGES.map((s) => <option key={s} value={s}>{dealStageMap[s].label}</option>)}
          </select>
        </Field>
        <Field label="Owner">
          <select className="input-base" value={data.owner_id || ""} onChange={(e) => set("owner_id", e.target.value)}>
            <option value="">—</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </Field>
        <Field label="Value (₹) *"><input className="input-base" type="number" value={data.value} data-testid="deal-value" onChange={(e) => set("value", e.target.value)} /></Field>
        <Field label="Capacity (kW)"><input className="input-base" type="number" value={data.capacity_kw ?? ""} onChange={(e) => set("capacity_kw", e.target.value)} /></Field>
        <Field label="Probability (%)"><input className="input-base" type="number" min="0" max="100" value={data.probability} onChange={(e) => set("probability", e.target.value)} /></Field>
        <Field label="Expected Close">
          <input className="input-base" type="date" value={(data.expected_close_date || "").slice(0, 10)} onChange={(e) => set("expected_close_date", e.target.value)} />
        </Field>
      </div>
      <Field label="Notes"><textarea className="input-base min-h-[80px]" value={data.notes || ""} onChange={(e) => set("notes", e.target.value)} /></Field>
    </Drawer>
  );
}
